import { format, isValid, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Calendar } from "./ui/calendar";
import { FormControl } from "./ui/form";
import { Input } from "./ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

/**
 * Usage Example:
 *
 * <DatePickerSingle
 *   disabled={isLoading}
 *   selectedDate={defaultDate}
 *   onDateChange={(date) => handleDateChange(date, field.name)}
 * />
 *
 * Works best with this function for handling dates:
 * const handleDateChange = (date, name) => {
        if (!date) {
            ;
            return; // Early return if the date is null or undefined
        }
        

        setFormData(prevState => ({
            ...prevState,
            [name]: date.toLocaleString('sv').split(' ')[0], // Update the specific field (startDate, endDate, etc.)
        }));
    };
 */

export function DatePickerSingle({ onDateChange, selectedDate, disabled, placeHolder = "Välj datum", id }) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [stringDate, setStringDate] = useState("");
  const inputRef = useRef(null);

  const isDateDisabled = (date) => {
    return (
      date.getFullYear() > new Date().getFullYear() + 20 ||
      date < new Date("1900-01-01")
    );
  };

  const handleOnSelect = (date) => {
    if (!date || isDateDisabled(date)) {
      setStringDate("");
      if (onDateChange) onDateChange(undefined);
      return;
    }
    const formattedDate = format(date, "yyyy-MM-dd");
    setStringDate(formattedDate);
    setIsPopoverOpen(false);
    if (onDateChange) onDateChange(date);
  };

  const handleInputChange = (e) => {
    setStringDate(e.target.value);
  };

  const handleBlur = () => {
    if (!stringDate) {
      setStringDate("");
      if (onDateChange) onDateChange(undefined);
      return;
    }
    const parsedDate = parse(stringDate, "yyyy-MM-dd", new Date());
    if (isValid(parsedDate) && !isDateDisabled(parsedDate)) {
      setStringDate(format(parsedDate, "yyyy-MM-dd"));
      if (onDateChange) onDateChange(parsedDate);
    } else {
      setStringDate("");
      if (onDateChange) onDateChange(undefined);
    }
  };

  useEffect(() => {
    if (selectedDate) {
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      setStringDate(formattedDate);
    } else {
      setStringDate("");
    }
  }, [selectedDate]);

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <FormControl>
          <div className="relative w-full">
            <Input
              id={id}
              type="text"
              value={stringDate}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onClick={() => setIsPopoverOpen(true)}
              placeholder={placeHolder}
              ref={inputRef}
              disabled={disabled}
              autoComplete='off'
            />
            <CalendarIcon className="absolute right-3 top-2.5 h-4 w-4 opacity-50 pointer-events-none" />
          </div>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Calendar
          mode="single"
          selected={parse(stringDate, "yyyy-MM-dd", new Date())}
          defaultMonth={stringDate || undefined}
          onSelect={handleOnSelect}
          disabled={isDateDisabled}
        />
      </PopoverContent>
    </Popover>
  );
};


